import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Image as ImageIcon, Save, Eye, Smartphone, MonitorSmartphone, LogIn, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

const GoldButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);
const GhostButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 text-xs uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);

const LAYOUT_OPTIONS = [
    { id: "icon_and_text", label: "Logo + Text", hint: "Recommended — icon on the left, app name beside it" },
    { id: "icon_only",     label: "Logo only",   hint: "Hide the app name (good for square / strong logos)" },
    { id: "text_only",     label: "Text only",   hint: "Hide the icon (useful if you only want the wordmark)" },
];

export default function AdminBranding() {
    const branding = useBranding();
    const [form, setForm] = useState({
        app_name: "", app_owner: "", app_tagline: "",
        logo_display_height: 32, logo_layout: "icon_and_text",
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    const [uploadingOg, setUploadingOg] = useState(false);
    const [uploadingAppIcon, setUploadingAppIcon] = useState(false);
    const fileRef = useRef(null);
    const faviconRef = useRef(null);
    const ogRef = useRef(null);
    const appIconRef = useRef(null);

    useEffect(() => {
        setForm({
            app_name: branding.app_name || "",
            app_owner: branding.app_owner || "",
            app_tagline: branding.app_tagline || "",
            logo_display_height: Number(branding.logo_display_height) || 32,
            logo_layout: branding.logo_layout || "icon_and_text",
        });
    }, [branding.app_name, branding.app_owner, branding.app_tagline,
        branding.logo_display_height, branding.logo_layout]);

    const dirty = (
        form.app_name !== (branding.app_name || "") ||
        form.app_owner !== (branding.app_owner || "") ||
        form.app_tagline !== (branding.app_tagline || "") ||
        Number(form.logo_display_height) !== (Number(branding.logo_display_height) || 32) ||
        form.logo_layout !== (branding.logo_layout || "icon_and_text")
    );

    const save = async () => {
        if (!form.app_name.trim()) { toast.error("App name is required"); return; }
        setSaving(true);
        try {
            await api.patch("/platform/branding", {
                ...form,
                logo_display_height: Number(form.logo_display_height),
            });
            toast.success("Branding saved — refreshes everywhere within ~30 seconds");
            await branding.reload();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const pickFile = () => fileRef.current?.click();

    const uploadAsset = async (e, opts) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > opts.maxBytes) {
            toast.error(`File too large (${Math.round(file.size / 1024)} KB). Max ${opts.maxBytes / 1024} KB.`);
            e.target.value = ""; return;
        }
        if (!opts.allowed.includes(file.type)) {
            toast.error(`Unsupported format: ${file.type}. Use ${opts.formatLabel}.`);
            e.target.value = ""; return;
        }
        opts.setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/platform/branding/${opts.endpoint}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${opts.label} uploaded (${data.size_kb} KB)`);
            await branding.reload();
        } catch (err) { toast.error(apiError(err)); }
        finally { opts.setBusy(false); e.target.value = ""; }
    };

    const removeAsset = async (endpoint, label) => {
        if (!window.confirm(`Remove the current ${label}?`)) return;
        try {
            await api.delete(`/platform/branding/${endpoint}`);
            toast.success(`${label} removed`);
            await branding.reload();
        } catch (e) { toast.error(apiError(e)); }
    };

    const upload = (e) => uploadAsset(e, {
        endpoint: "logo", label: "Logo",
        allowed: ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"],
        formatLabel: "PNG, JPG, SVG or WEBP", maxBytes: 1024 * 1024, setBusy: setUploading,
    });
    const remove = () => removeAsset("logo", "Logo");
    const uploadFavicon = (e) => uploadAsset(e, {
        endpoint: "favicon", label: "Favicon",
        allowed: ["image/png", "image/jpeg", "image/svg+xml", "image/webp", "image/x-icon", "image/vnd.microsoft.icon"],
        formatLabel: "PNG, ICO, SVG or WEBP", maxBytes: 256 * 1024, setBusy: setUploadingFavicon,
    });
    const uploadOg = (e) => uploadAsset(e, {
        endpoint: "og-image", label: "OG image",
        allowed: ["image/png", "image/jpeg", "image/webp"],
        formatLabel: "PNG, JPG or WEBP", maxBytes: 2 * 1024 * 1024, setBusy: setUploadingOg,
    });
    const uploadAppIcon = (e) => uploadAsset(e, {
        endpoint: "app-icon", label: "App icon",
        allowed: ["image/png", "image/jpeg", "image/webp"],
        formatLabel: "PNG, JPG or WEBP", maxBytes: 1024 * 1024, setBusy: setUploadingAppIcon,
    });

    // Preview helpers
    const previewH = Math.max(24, Math.min(72, Number(form.logo_display_height) || 32));
    const previewLayout = form.logo_layout || "icon_and_text";
    const showIcon = previewLayout !== "text_only";
    const showText = previewLayout !== "icon_only";

    return (
        <div className="space-y-6 max-w-5xl" data-testid="admin-branding-page">
            <div>
                <div className="overline">Platform</div>
                <h1 className="display-display mt-1">Branding</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    One place to control the logo, app name, owner attribution and tagline shown across the platform — sidebar, login, public landing, generated PDFs, mobile installs.
                    Changes propagate everywhere within ~30 seconds, including the installed mobile PWA (Chrome auto-refreshes its manifest daily).
                </p>
            </div>

            {/* ════ LOGO + DISPLAY CONTROLS (the main card) ════════════════════════ */}
            <div className="bg-muted border border-border p-6 space-y-6" data-testid="logo-card">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="overline">App logo</div>
                        <div className="text-[11px] text-muted-foreground mt-1">Upload once · re-renders everywhere</div>
                    </div>
                    {branding.logo_src && (
                        <a href={branding.logo_src} target="_blank" rel="noopener noreferrer"
                            data-testid="logo-view-link"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-primary hover:text-primary text-[10px] uppercase tracking-[0.15em]">
                            <Eye className="w-3 h-3"/>View original
                        </a>
                    )}
                </div>

                {/* Upload row */}
                <div className="grid lg:grid-cols-[180px_1fr] gap-6">
                    <div className="bg-background border border-border h-44 lg:h-full flex items-center justify-center" data-testid="logo-preview">
                        {branding.logo_src ? (
                            <img src={branding.logo_src} alt={branding.app_name} className="max-w-full max-h-full object-contain p-4" data-testid="logo-preview-img"/>
                        ) : (
                            <div className="text-center">
                                <ImageIcon className="w-10 h-10 text-border mx-auto" strokeWidth={1.2}/>
                                <div className="text-[10px] text-[#5A5A5A] mt-2 uppercase tracking-[0.15em]">No logo set</div>
                            </div>
                        )}
                    </div>
                    <div className="space-y-3">
                        <div className="bg-background border border-primary/30 p-3 text-[11px] text-muted-foreground leading-relaxed">
                            <div className="text-primary uppercase tracking-[0.15em] text-[10px] mb-1.5">Recommended specs</div>
                            <ul className="space-y-0.5">
                                <li>• Square <span className="text-foreground">512 × 512 px (1:1)</span> for icon-style logos · landscape <span className="text-foreground">600 × 200 px (3:1)</span> for wordmarks</li>
                                <li>• Transparent background preferred · solid white also fine</li>
                                <li>• Formats: <span className="text-foreground">PNG, SVG, WEBP, JPG</span> · max <span className="text-foreground">1 MB</span></li>
                                <li>• High contrast so the logo reads at 24-72 px on the topbar</li>
                            </ul>
                        </div>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={upload} className="hidden" data-testid="logo-file-input"/>
                        <div className="flex gap-2 flex-wrap">
                            <GoldButton onClick={pickFile} disabled={uploading} data-testid="logo-upload-btn">
                                <Upload className="w-3.5 h-3.5"/>{uploading ? "Uploading…" : (branding.logo_src ? "Replace logo" : "Upload logo")}
                            </GoldButton>
                            {branding.logo_src && (
                                <GhostButton onClick={remove} data-testid="logo-remove-btn">
                                    <Trash2 className="w-3.5 h-3.5"/>Remove
                                </GhostButton>
                            )}
                        </div>
                    </div>
                </div>

                {/* DISPLAY CONTROLS — size + layout */}
                <div className="pt-5 border-t border-border space-y-5">
                    <div>
                        <div className="overline text-primary">Display size · {previewH} px</div>
                        <p className="text-[11px] text-muted-foreground mt-1 mb-3">
                            Controls how tall the logo renders in the topbar, login page, and registration page. Pixel value · drag the slider for live preview.
                        </p>
                        <div className="flex items-center gap-4">
                            <input type="range" min="24" max="72" step="2"
                                value={form.logo_display_height}
                                onChange={e => setForm({ ...form, logo_display_height: Number(e.target.value) })}
                                data-testid="logo-height-slider"
                                className="flex-1 accent-primary"/>
                            <input type="number" min="24" max="72"
                                value={form.logo_display_height}
                                onChange={e => setForm({ ...form, logo_display_height: Number(e.target.value) })}
                                data-testid="logo-height-input"
                                className="w-20 bg-background border border-border focus:border-primary outline-none px-2 py-1.5 text-sm text-center"/>
                            <span className="text-[11px] text-muted-foreground">px</span>
                        </div>
                        <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                            <span>24 (compact)</span>
                            <span>32 (default)</span>
                            <span>48 (large)</span>
                            <span>72 (max)</span>
                        </div>
                    </div>

                    <div>
                        <div className="overline text-primary">Display layout</div>
                        <p className="text-[11px] text-muted-foreground mt-1 mb-3">
                            Choose what shows next to your logo across the platform.
                        </p>
                        <div className="grid sm:grid-cols-3 gap-2">
                            {LAYOUT_OPTIONS.map(o => (
                                <label key={o.id}
                                    className={`cursor-pointer p-3 border transition-colors ${form.logo_layout === o.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                                    data-testid={`logo-layout-${o.id}`}>
                                    <input type="radio" name="logo_layout" value={o.id}
                                        checked={form.logo_layout === o.id}
                                        onChange={() => setForm({ ...form, logo_layout: o.id })}
                                        className="sr-only"/>
                                    <div className="text-xs font-medium text-foreground mb-1">{o.label}</div>
                                    <div className="text-[10px] text-muted-foreground leading-snug">{o.hint}</div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* MULTI-CONTEXT LIVE PREVIEW */}
                <div className="pt-5 border-t border-border space-y-4">
                    <div className="flex items-center gap-2">
                        <div className="overline text-primary">Live preview · where your logo appears</div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        {/* Topbar preview */}
                        <PreviewCard icon={MonitorSmartphone} label="Desktop / Tablet topbar">
                            <div className="bg-card border border-border h-16 px-4 flex items-center justify-between" data-testid="preview-topbar">
                                <LogoMark logoSrc={branding.logo_src} appName={form.app_name || "Law OS"}
                                    height={previewH} showIcon={showIcon} showText={showText}
                                    textVariant="topbar"/>
                                <div className="hidden sm:flex gap-1 opacity-50">
                                    <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
                                    <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
                                </div>
                            </div>
                        </PreviewCard>
                        {/* Login preview */}
                        <PreviewCard icon={LogIn} label="Login + Register pages">
                            <div className="bg-background border border-border h-16 px-4 flex items-center" data-testid="preview-login">
                                <LogoMark logoSrc={branding.logo_src} appName={form.app_name || "Law OS"}
                                    height={previewH} showIcon={showIcon} showText={showText}
                                    textVariant="serif"/>
                            </div>
                        </PreviewCard>
                        {/* Mobile preview */}
                        <PreviewCard icon={Smartphone} label="Mobile (PWA / browser)">
                            <div className="bg-card border border-border h-16 px-3 flex items-center justify-between" data-testid="preview-mobile">
                                <LogoMark logoSrc={branding.logo_src} appName={form.app_name || "Law OS"}
                                    height={Math.min(previewH, 40)} showIcon={showIcon} showText={false}
                                    textVariant="topbar"/>
                                <div className="flex gap-2 opacity-50">
                                    <div className="w-4 h-0.5 bg-foreground"></div>
                                </div>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1 px-1">App name always hidden on mobile — icon does the work.</div>
                        </PreviewCard>
                    </div>
                </div>
            </div>

            {/* App Icon — separate from website logo, used ONLY for PWA install */}
            <div className="bg-muted border border-border p-6 space-y-4" data-testid="app-icon-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="overline">App icon (Mobile / PWA)</div>
                        <div className="text-[11px] text-muted-foreground mt-1 max-w-xl leading-relaxed">
                            Separate icon used ONLY when {form.app_name || "Law OS"} is installed as a mobile app (Add to Home Screen).
                            Upload a distinct square icon here — usually with a white or solid background — so the mobile
                            app icon looks crisp instead of shrunken. Leave blank to fall back to the website logo.
                        </div>
                    </div>
                    {branding.app_icon_src && (
                        <a href={branding.app_icon_src} target="_blank" rel="noopener noreferrer"
                            data-testid="app-icon-view-link"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-primary hover:text-primary text-[10px] uppercase tracking-[0.15em]">
                            <Eye className="w-3 h-3"/>View original
                        </a>
                    )}
                </div>

                <div className="grid lg:grid-cols-[200px_1fr] gap-6 items-start">
                    {/* Preview — rendered on a home-screen-mimicking mock */}
                    <div className="space-y-2">
                        <div className="bg-background border border-border p-4 flex flex-col items-center gap-2" data-testid="app-icon-preview">
                            <div className="w-24 h-24 rounded-[22px] shadow-lawos-md bg-white overflow-hidden flex items-center justify-center border border-border">
                                {branding.app_icon_src ? (
                                    <img src={branding.app_icon_src} alt="app icon"
                                        className="w-full h-full object-contain"
                                        data-testid="app-icon-preview-img"/>
                                ) : branding.logo_src ? (
                                    <img src={branding.logo_src} alt="fallback"
                                        className="w-4/5 h-4/5 object-contain opacity-90"
                                        data-testid="app-icon-preview-fallback"/>
                                ) : (
                                    <ImageIcon className="w-8 h-8 text-border" strokeWidth={1.2}/>
                                )}
                            </div>
                            <div className="text-[10px] text-muted-foreground font-medium mt-1">
                                {form.app_name || "Law OS"}
                            </div>
                            <div className="text-[9px] text-[#5A5A5A] uppercase tracking-[0.15em]">
                                {branding.app_icon_src ? "Live · app icon" : (branding.logo_src ? "Fallback · using logo" : "Not set")}
                            </div>
                        </div>
                        <div className="text-[10px] text-muted-foreground text-center italic">
                            Same shape iOS + Android use on the home screen.
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="bg-background border border-primary/30 p-3 text-[11px] text-muted-foreground leading-relaxed">
                            <div className="text-primary uppercase tracking-[0.15em] text-[10px] mb-1.5">Recommended specs</div>
                            <ul className="space-y-0.5">
                                <li>• <span className="text-foreground">Square 512 × 512 px</span> (Android maskable + iOS home screen both use this)</li>
                                <li>• <span className="text-foreground">Solid background</span> — white or your brand colour, NOT transparent</li>
                                <li>• Keep the logo mark centred with <span className="text-foreground">~10% padding</span> on all sides (Android crops the corners)</li>
                                <li>• Formats: <span className="text-foreground">PNG, JPG, WEBP</span> · max <span className="text-foreground">1 MB</span></li>
                                <li>• Different from the website logo (which is usually a horizontal wordmark on transparent bg)</li>
                            </ul>
                        </div>
                        <input ref={appIconRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadAppIcon} className="hidden" data-testid="app-icon-file-input"/>
                        <div className="flex gap-2 flex-wrap">
                            <GoldButton onClick={() => appIconRef.current?.click()} disabled={uploadingAppIcon} data-testid="app-icon-upload-btn">
                                <Upload className="w-3.5 h-3.5"/>{uploadingAppIcon ? "Uploading…" : (branding.app_icon_src ? "Replace app icon" : "Upload app icon")}
                            </GoldButton>
                            {branding.app_icon_src && (
                                <GhostButton onClick={() => removeAsset("app-icon", "App icon")} data-testid="app-icon-remove-btn">
                                    <Trash2 className="w-3.5 h-3.5"/>Remove
                                </GhostButton>
                            )}
                        </div>
                        <div className="text-[10px] text-muted-foreground leading-relaxed">
                            <strong className="text-foreground">Note:</strong> Chrome refreshes the manifest daily. Installed users
                            will see the new icon within 24 hours OR they can uninstall + re-install for instant refresh.
                        </div>
                    </div>
                </div>
            </div>

            {/* Favicon */}
            <div className="bg-muted border border-border p-6 space-y-4" data-testid="favicon-card">
                <div className="overline">Favicon</div>
                <div className="flex items-start gap-6 flex-wrap">
                    <div className="bg-background border border-border w-24 h-24 flex items-center justify-center" data-testid="favicon-preview">
                        {branding.favicon_src ? (
                            <img src={branding.favicon_src} alt="favicon" className="max-w-full max-h-full object-contain p-3" data-testid="favicon-preview-img"/>
                        ) : (
                            <div className="text-center">
                                <ImageIcon className="w-6 h-6 text-border mx-auto" strokeWidth={1.2}/>
                                <div className="text-[10px] text-[#5A5A5A] mt-1 uppercase tracking-[0.15em]">Falls back to logo</div>
                            </div>
                        )}
                    </div>
                    <div className="flex-1 min-w-[280px] space-y-3">
                        <div className="bg-background border border-primary/30 p-3 text-[11px] text-muted-foreground leading-relaxed">
                            <div className="text-primary uppercase tracking-[0.15em] text-[10px] mb-1.5">Recommended specs</div>
                            <ul className="space-y-0.5">
                                <li>• Square <span className="text-foreground">32×32 to 192×192 px</span></li>
                                <li>• Formats: <span className="text-foreground">PNG, ICO, SVG, WEBP</span> · max <span className="text-foreground">256 KB</span></li>
                                <li>• Leave blank to use the logo as favicon</li>
                            </ul>
                        </div>
                        <input ref={faviconRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/x-icon" onChange={uploadFavicon} className="hidden" data-testid="favicon-file-input"/>
                        <div className="flex gap-2 flex-wrap">
                            <GoldButton onClick={() => faviconRef.current?.click()} disabled={uploadingFavicon} data-testid="favicon-upload-btn">
                                <Upload className="w-3.5 h-3.5"/>{uploadingFavicon ? "Uploading…" : (branding.favicon_src ? "Replace favicon" : "Upload favicon")}
                            </GoldButton>
                            {branding.favicon_src && (
                                <GhostButton onClick={() => removeAsset("favicon", "Favicon")} data-testid="favicon-remove-btn">
                                    <Trash2 className="w-3.5 h-3.5"/>Remove
                                </GhostButton>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* OG Image */}
            <div className="bg-muted border border-border p-6 space-y-4" data-testid="og-image-card">
                <div className="overline">Social share image (Open Graph)</div>
                <p className="text-[11px] text-muted-foreground leading-relaxed -mt-2">
                    The preview image WhatsApp, Slack, Twitter and LinkedIn use when someone shares a {form.app_name || "Law OS"} link. Falls back to the favicon, then the logo.
                </p>
                <div className="flex items-start gap-6 flex-wrap">
                    <div className="bg-background border border-border w-48 h-[100px] flex items-center justify-center" data-testid="og-preview">
                        {branding.og_image_src ? (
                            <img src={branding.og_image_src} alt="og" className="max-w-full max-h-full object-contain" data-testid="og-preview-img"/>
                        ) : (
                            <div className="text-center">
                                <ImageIcon className="w-7 h-7 text-border mx-auto" strokeWidth={1.2}/>
                                <div className="text-[10px] text-[#5A5A5A] mt-1 uppercase tracking-[0.15em]">No OG image</div>
                            </div>
                        )}
                    </div>
                    <div className="flex-1 min-w-[280px] space-y-3">
                        <div className="bg-background border border-primary/30 p-3 text-[11px] text-muted-foreground leading-relaxed">
                            <div className="text-primary uppercase tracking-[0.15em] text-[10px] mb-1.5">Recommended specs</div>
                            <ul className="space-y-0.5">
                                <li>• Landscape <span className="text-foreground">1200 × 630 px (1.91:1)</span></li>
                                <li>• Centred content (safe area: middle 80%)</li>
                                <li>• Formats: <span className="text-foreground">PNG, JPG, WEBP</span> · max <span className="text-foreground">2 MB</span></li>
                            </ul>
                        </div>
                        <input ref={ogRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadOg} className="hidden" data-testid="og-file-input"/>
                        <div className="flex gap-2 flex-wrap">
                            <GoldButton onClick={() => ogRef.current?.click()} disabled={uploadingOg} data-testid="og-upload-btn">
                                <Upload className="w-3.5 h-3.5"/>{uploadingOg ? "Uploading…" : (branding.og_image_src ? "Replace OG image" : "Upload OG image")}
                            </GoldButton>
                            {branding.og_image_src && (
                                <GhostButton onClick={() => removeAsset("og-image", "OG image")} data-testid="og-remove-btn">
                                    <Trash2 className="w-3.5 h-3.5"/>Remove
                                </GhostButton>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Text identity */}
            <div className="bg-muted border border-border p-6 space-y-4" data-testid="text-fields-card">
                <div className="overline">App identity</div>
                <Field label="App name *" hint="Shown in the sidebar, login page, browser tab.">
                    <Input value={form.app_name} onChange={e => setForm({ ...form, app_name: e.target.value })} data-testid="app-name-input" placeholder="Law OS"/>
                </Field>
                <Field label="Owner attribution" hint="Shown in the sidebar footer and public landing page.">
                    <Input value={form.app_owner} onChange={e => setForm({ ...form, app_owner: e.target.value })} data-testid="app-owner-input" placeholder="Inno Aim Private Limited"/>
                </Field>
                <Field label="Tagline" hint="Shown on public marketing pages + OG meta description.">
                    <Input value={form.app_tagline} onChange={e => setForm({ ...form, app_tagline: e.target.value })} data-testid="app-tagline-input"/>
                </Field>
            </div>

            {/* Sticky save bar */}
            <div className="sticky bottom-4 bg-card/95 backdrop-blur border border-border p-4 flex items-center justify-between gap-4 flex-wrap shadow-lawos-md">
                <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                    {dirty ? (
                        <>
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500"/>
                            You have unsaved changes — click <strong className="text-foreground">Save branding</strong> to apply them everywhere.
                        </>
                    ) : (
                        <>All branding settings are saved & live.</>
                    )}
                </div>
                <GoldButton onClick={save} disabled={saving || !dirty} data-testid="branding-save-btn">
                    <Save className="w-3.5 h-3.5"/>{saving ? "Saving…" : "Save branding"}
                </GoldButton>
            </div>
        </div>
    );
}

// ── Local building blocks ─────────────────────────────────────────────────────

function PreviewCard({ icon: Icon, label, children }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">
                <Icon className="w-3 h-3" strokeWidth={1.5}/>{label}
            </div>
            {children}
        </div>
    );
}

function LogoMark({ logoSrc, appName, height, showIcon, showText, textVariant }) {
    const [n1, ...rest] = (appName || "Law OS").split(" ");
    const n2 = rest.join(" ");
    return (
        <div className="flex items-center gap-2.5 min-w-0">
            {showIcon && (logoSrc ? (
                <img src={logoSrc} alt={appName}
                    style={{ height, width: "auto", maxWidth: height * 2.5 }}
                    className="object-contain shrink-0"/>
            ) : (
                <div style={{ height, width: height }}
                    className="rounded-lg bg-primary flex items-center justify-center shrink-0">
                    <span className="font-heading text-primary-foreground font-bold"
                        style={{ fontSize: height * 0.45 }}>{(appName || "L")[0]}</span>
                </div>
            ))}
            {showText && (
                textVariant === "serif" ? (
                    <div className="font-serif text-[15px] truncate">{appName}</div>
                ) : (
                    <div className="min-w-0">
                        <div className="font-heading text-[15px] leading-none font-semibold tracking-tight truncate">
                            {n1}{n2 && <span className="text-primary"> {n2}</span>}
                        </div>
                    </div>
                )
            )}
        </div>
    );
}

const Field = ({ label, children, hint }) => (
    <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 block">{label}</label>
        {children}
        {hint && <div className="text-[10px] text-[#5A5A5A] mt-1">{hint}</div>}
    </div>
);
const Input = (p) => (
    <input {...p} className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm transition-colors"/>
);
